import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Modal, Button, ButtonVariant, ButtonStyle, H3 } from '@meiuca/ui-components'
import type { Modality } from '@/types/modality'

interface DayUseFilterModalProps {
  open: boolean
  onClose: () => void
  modalities: Modality[]
  selectedModalityIds: Set<string>
  selectedDate: string | null
  onApplyFilters: (modalityIds: Set<string>, date: string | null) => void
  onClearFilters: () => void
}

export function DayUseFilterModal({
  open,
  onClose,
  modalities,
  selectedModalityIds,
  selectedDate,
  onApplyFilters,
  onClearFilters,
}: DayUseFilterModalProps) {
  const [tempSelectedModalityIds, setTempSelectedModalityIds] = useState<Set<string>>(selectedModalityIds)
  const [tempSelectedDate, setTempSelectedDate] = useState<string | null>(selectedDate)

  useEffect(() => {
    setTempSelectedModalityIds(new Set(selectedModalityIds))
    setTempSelectedDate(selectedDate)
  }, [selectedModalityIds, selectedDate])

  const toggleModality = (modalityId: string) => {
    const newSet = new Set(tempSelectedModalityIds)
    if (newSet.has(modalityId)) {
      newSet.delete(modalityId)
    } else {
      newSet.add(modalityId)
    }
    setTempSelectedModalityIds(newSet)
  }

  const handleApply = () => {
    onApplyFilters(tempSelectedModalityIds, tempSelectedDate)
    onClose()
  }

  const handleClear = () => {
    setTempSelectedModalityIds(new Set())
    setTempSelectedDate(null)
    onClearFilters()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={true}
      showCloseButton={false}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4 border-b border-border-light">
          <H3>Filtros</H3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-tertiary" />
          </button>
        </div>

        <div>
          <h4 className="font-semibold text-text-primary mb-3">Dia</h4>
          <input
            type="date"
            value={tempSelectedDate ?? ''}
            onChange={(event) => setTempSelectedDate(event.target.value || null)}
            className="w-full px-3 py-2 border border-border-light rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>

        <div>
          <h4 className="font-semibold text-text-primary mb-3">Modalidades</h4>
          <div className="grid grid-cols-2 gap-2">
            {modalities.map((modality) => {
              const isSelected = tempSelectedModalityIds.has(modality.id)
              return (
                <label
                  key={modality.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${
                      isSelected
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-border-light hover:border-brand-primary/50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleModality(modality.id)}
                    className="w-4 h-4 text-brand-primary rounded border-border-primary focus:ring-brand-primary"
                  />
                  <span className="text-sm text-text-primary">{modality.description}</span>
                </label>
              )
            })}
          </div>

          {modalities.length === 0 && (
            <p className="text-center text-text-secondary py-4">Carregando modalidades...</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border-light">
          <Button buttonStyle={ButtonStyle.BORDERED} onClick={handleClear}>
            Limpar filtros
          </Button>
          <Button variant={ButtonVariant.PRIMARY} onClick={handleApply}>
            Aplicar filtros
          </Button>
        </div>
      </div>
    </Modal>
  )
}
